import { ArrowRight, Lock, Mail, User, Building2, Phone, Eye, EyeOff, Shield, HardHat, Briefcase } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAuth } from '../auth/AuthProviderUnified';
import AuthLayout from '../components/auth/AuthLayout';
import ErrorBoundary from '../components/ErrorBoundary';
import PlanSelector from '../components/onboarding/PlanSelector';
import ProcoreConnectPrompt from '../components/onboarding/ProcoreConnectPrompt';
import { api } from '../services/api/client';

/** ?role= values accepted on /signup (the /sc-onboard redirect sends role=sc). */
const ROLE_PARAM_TO_OPTION = {
  gc: 'gcAdmin',
  gcadmin: 'gcAdmin',
  sc: 'scAdmin',
  scadmin: 'scAdmin',
};

const getOrganizationIdFromUser = (registeredUser) => {
  if (!registeredUser) return null;
  if (registeredUser.organizationId) {
    return typeof registeredUser.organizationId === 'object'
      ? (registeredUser.organizationId._id || registeredUser.organizationId.id)
      : registeredUser.organizationId;
  }
  if (registeredUser.organization?._id) return registeredUser.organization._id;
  if (registeredUser.organization?.id) return registeredUser.organization.id;
  return null;
};

export default function GetStarted() {
  const inputClass = "w-full px-4 py-3 bg-white border border-black/10 rounded-xl text-[#1C1C1E] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF6A00]/50 focus:border-[#FF6A00]/50 transition-colors";

  // ?role=sc|gc|scAdmin|gcAdmin preselects the account type and ?company=
  // prefills the organization name (both are sent by the /sc-onboard redirect)
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState(() => ({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    organizationName: searchParams.get('company') || '',
    role: ROLE_PARAM_TO_OPTION[(searchParams.get('role') || '').toLowerCase()] || '',
    plan: 'beta',
  }));

  const roleOptions = [
    { value: 'gcAdmin', label: 'General Contractor', accountType: 'organization', icon: HardHat, description: 'Manage projects, crews, and safety compliance' },
    { value: 'scAdmin', label: 'Sub Contractor', accountType: 'organization', icon: Briefcase, description: 'Track your team and jobsite assignments' },
  ];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showProcoreConnectOffer, setShowProcoreConnectOffer] = useState(false);
  const [registrationSucceeded, setRegistrationSucceeded] = useState(false);
  const navigate = useNavigate();
  const { register, user } = useAuth();
  /** Prevents double-submit before React re-renders `loading` (avoids second Firebase createUser → email-already-in-use). */
  const registrationInFlightRef = useRef(false);

  useEffect(() => {
    // Redirect when context has completed backend shape (e.g. user returns to
    // /signup while logged in) — but NOT while a registration is in flight or
    // the post-signup Procore offer is on screen. Without those guards this
    // effect fires the moment the auth context hydrates after register() and
    // navigates away before the Procore offer ever renders.
    if (registrationInFlightRef.current || registrationSucceeded || showProcoreConnectOffer) return;
    if (user && user.role !== 'worker' && user.organizationId) {
      navigate('/command-center', { replace: true });
    }
  }, [user, navigate, registrationSucceeded, showProcoreConnectOffer]);

  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (error) setError('');
      setEmailAlreadyExists(false);
      // Clear the inline error for the field being edited
      setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
    },
    [error]
  );

  const handleFirstNameChange = useCallback((e) => handleInputChange('firstName', e.target.value), [handleInputChange]);
  const handleLastNameChange = useCallback((e) => handleInputChange('lastName', e.target.value), [handleInputChange]);
  const handleEmailChange = useCallback((e) => handleInputChange('email', e.target.value), [handleInputChange]);
  const handlePhoneChange = useCallback((e) => handleInputChange('phone', e.target.value), [handleInputChange]);
  const handlePasswordChange = useCallback((e) => handleInputChange('password', e.target.value), [handleInputChange]);
  const handleConfirmPasswordChange = useCallback((e) => handleInputChange('confirmPassword', e.target.value), [handleInputChange]);
  const handleOrganizationNameChange = useCallback((e) => handleInputChange('organizationName', e.target.value), [handleInputChange]);
  const handleRoleChange = useCallback((role) => handleInputChange('role', role), [handleInputChange]);
  const handlePlanChange = useCallback((plan) => handleInputChange('plan', plan), [handleInputChange]);

  const handleTogglePasswordVisibility = useCallback(() => setShowPassword(!showPassword), [showPassword]);
  const handleToggleConfirmPasswordVisibility = useCallback(() => setShowConfirmPassword(!showConfirmPassword), [showConfirmPassword]);

  /** Per-field validation used on blur and on submit (returns a message or null). */
  const validateField = useCallback((field, values) => {
    switch (field) {
      case 'firstName':
        return values.firstName.trim() ? null : 'First name is required';
      case 'lastName':
        return values.lastName.trim() ? null : 'Last name is required';
      case 'email':
        if (!values.email.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email';
        return null;
      case 'password':
        if (!values.password) return 'Password is required';
        if (values.password.length < 6) return 'Password must be at least 6 characters';
        return null;
      case 'confirmPassword':
        if (!values.confirmPassword) return 'Please confirm your password';
        if (values.password !== values.confirmPassword) return 'Passwords do not match';
        return null;
      case 'organizationName':
        return values.organizationName.trim() ? null : 'Organization name is required';
      default:
        return null;
    }
  }, []);

  /** Show inline feedback as soon as the user leaves a field. */
  const handleFieldBlur = useCallback(
    (field) => {
      setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, formData) }));
    },
    [formData, validateField]
  );

  const validateForm = useCallback(() => {
    const errors = {};
    ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'organizationName'].forEach(
      (field) => {
        const message = validateField(field, formData);
        if (message) errors[field] = message;
      }
    );

    let formError = null;
    if (!formData.role) formError = 'Please select your account type';
    else if (formData.plan !== 'beta') formError = "Payments are not integrated yet. Select the beta plan for now.";

    return { errors, formError };
  }, [formData, validateField]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { errors: validationErrors, formError } = validateForm();
      const hasFieldErrors = Object.values(validationErrors).some(Boolean);
      if (hasFieldErrors || formError) {
        setFieldErrors(validationErrors);
        setError(formError || 'Please fix the highlighted fields');
        return;
      }

      if (registrationInFlightRef.current) {
        return;
      }
      registrationInFlightRef.current = true;
      setLoading(true);
      setError('');
      setWarning('');
      setEmailAlreadyExists(false);

      try {
        const selectedRole = roleOptions.find(r => r.value === formData.role);
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          role: formData.role,
          accountType: selectedRole?.accountType || 'organization',
          organizationName: formData.organizationName,
          selectedPlan: formData.plan,
        };

        const registeredUser = await register(formData.email, formData.password, userData);
        setRegistrationSucceeded(true);
        const organizationId = getOrganizationIdFromUser(registeredUser);
        if (organizationId) {
          try {
            await api.post(`/api/organizations/${organizationId}/select-plan`, { plan: formData.plan });
          } catch (_planError) {
            setWarning('Your account was created, but the plan step could not be saved. You can try again from Settings.');
          }
        }
        setSuccessMessage('Account created! You can connect Procore now or from Settings later.');
        setShowProcoreConnectOffer(true);
      } catch (err) {
        // Map API failures honestly instead of parroting a generic message
        if (
          err?.status === 409 ||
          err?.code === 'auth/email-already-in-use' ||
          /already exists/i.test(err?.message || '')
        ) {
          setEmailAlreadyExists(true);
          setError('An account with this email already exists.');
        } else if (Array.isArray(err?.errors) && err.errors.length > 0) {
          // universalValidation 400 payload: {message, errors: [{field, message}]}
          const apiFieldErrors = {};
          const unmappedMessages = [];
          err.errors.forEach((detail) => {
            if (detail?.field && Object.prototype.hasOwnProperty.call(formData, detail.field)) {
              apiFieldErrors[detail.field] = detail.message;
            } else if (detail?.message) {
              unmappedMessages.push(detail.message);
            }
          });
          if (Object.keys(apiFieldErrors).length > 0) {
            setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
          }
          setError(
            unmappedMessages.length > 0
              ? unmappedMessages.join(' ')
              : 'Please fix the highlighted fields'
          );
        } else {
          setError(err.message || 'Registration failed. Please try again.');
        }
      } finally {
        registrationInFlightRef.current = false;
        setLoading(false);
      }
    },
    [formData, register, validateForm, navigate]
  );

  const handleFormSubmit = useCallback((e) => handleSubmit(e), [handleSubmit]);

  return (
    <AuthLayout topRightText="Already have an account?" topRightTo="/login" topRightCta="Sign in">
      <div className="relative w-full max-w-5xl">
        {/* Ambient brand glow */}
        <div className="absolute -inset-4 bg-[#FF6A00]/10 blur-3xl rounded-[2rem] opacity-60 pointer-events-none" />

        {/* Registration Card */}
        <div className="relative bg-white vh-edge-light rounded-3xl p-10 space-y-8 overflow-hidden">
          <div className="relative">
            {/* Header */}
            <div className="text-center space-y-3 mb-10">
              <h1 className="text-3xl font-bold text-[#1C1C1E] tracking-tight">Create your account</h1>
              <p className="text-slate-600 text-sm font-medium">Sign up for your Virtual Hardhat account</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="text-red-600 text-xs">!</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{error}</p>
                    {emailAlreadyExists && (
                      <p className="text-sm mt-1">
                        <Link to="/login" className="text-[#FF6A00] font-semibold hover:text-orange-500 underline">
                          Sign in instead
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {warning && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6" role="status">
                <p className="text-sm font-medium">{warning}</p>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-600 text-xs">&#10003;</span>
                  </div>
                  <p className="text-sm font-medium text-emerald-700 flex-1">{successMessage}</p>
                </div>
              </div>
            )}

            {showProcoreConnectOffer && (
              <ErrorBoundary
                errorTitle="Continue to your dashboard"
                errorMessage="Something went wrong showing the optional Procore step. You can continue to the dashboard."
                fallback={(
                  <div className="mt-6 rounded-xl border border-black/10 bg-black/[0.03] p-6 text-center">
                    <button
                      type="button"
                      onClick={() => navigate('/command-center', { replace: true })}
                      className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 text-sm font-semibold text-white"
                    >
                      Continue to dashboard
                    </button>
                  </div>
                )}
              >
                <ProcoreConnectPrompt
                  connectHref="/api/procore/connect?grantType=authorization_code&source=signup"
                  onSkip={() => navigate('/command-center', { replace: true })}
                />
              </ErrorBoundary>
            )}

            {/* Registration Form */}
            {!showProcoreConnectOffer && (
            <form onSubmit={handleFormSubmit} className="space-y-8">
              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Personal & Company Info */}
                <div className="space-y-6">
                  {/* Personal Information Section */}
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-black/10">
                      <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                        <User className="h-4 w-4 text-[#FF6A00]" />
                        Personal Information
                      </h2>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="firstName" className="block text-sm font-semibold text-slate-700">
                          First Name
                        </label>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={handleFirstNameChange}
                          onBlur={() => handleFieldBlur('firstName')}
                          aria-invalid={!!fieldErrors.firstName}
                          className={inputClass}
                          placeholder="John"
                          required
                        />
                        {fieldErrors.firstName && (
                          <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.firstName}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="lastName" className="block text-sm font-semibold text-slate-700">
                          Last Name
                        </label>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={handleLastNameChange}
                          onBlur={() => handleFieldBlur('lastName')}
                          aria-invalid={!!fieldErrors.lastName}
                          className={inputClass}
                          placeholder="Doe"
                          required
                        />
                        {fieldErrors.lastName && (
                          <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleEmailChange}
                        onBlur={() => handleFieldBlur('email')}
                        aria-invalid={!!fieldErrors.email}
                        className={inputClass}
                        placeholder="you@company.com"
                        required
                      />
                      {fieldErrors.email && (
                        <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Phone Number <span className="text-slate-500 font-normal text-xs">(Optional)</span>
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className={inputClass}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Company Information Section */}
                  <div className="space-y-4 pt-4">
                    <div className="pb-3 border-b border-black/10">
                      <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-[#FF6A00]" />
                        Company Information
                      </h2>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="organizationName" className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Company Name
                      </label>
                      <input
                        id="organizationName"
                        name="organizationName"
                        type="text"
                        value={formData.organizationName}
                        onChange={handleOrganizationNameChange}
                        onBlur={() => handleFieldBlur('organizationName')}
                        aria-invalid={!!fieldErrors.organizationName}
                        className={inputClass}
                        placeholder="Your Company LLC"
                        required
                      />
                      {fieldErrors.organizationName && (
                        <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.organizationName}</p>
                      )}
                    </div>

                    {/* Account Type Selection */}
                    <div className="space-y-3 pt-2">
                      <label className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Account Type <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {roleOptions.map((option) => {
                          const IconComponent = option.icon;
                          const isSelected = formData.role === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleRoleChange(option.value)}
                              className={`w-full p-3 rounded-xl border transition-colors text-left flex items-center gap-3 ${
                                isSelected
                                  ? 'bg-[#FF6A00]/15 border-[#FF6A00]/40 ring-2 ring-[#FF6A00]/30'
                                  : 'bg-black/[0.03] border-black/10 hover:border-[#FF6A00]/30'
                              }`}
                            >
                              <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#FF6A00]/30' : 'bg-black/[0.03]'}`}>
                                <IconComponent className={`h-5 w-5 ${isSelected ? 'text-[#FF6A00]' : 'text-slate-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`font-semibold text-sm ${isSelected ? 'text-[#FF6A00]' : 'text-slate-800'}`}>
                                  {option.label}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                  {option.description}
                                </div>
                              </div>
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-[#FF6A00] bg-[#FF6A00]' : 'border-slate-300'
                              }`}>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-white" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Account Security & Benefits */}
                <div className="space-y-6">
                  {/* Account Security Section */}
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-black/10">
                      <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                        <Lock className="h-4 w-4 text-[#FF6A00]" />
                        Account Security
                      </h2>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Lock className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handlePasswordChange}
                          onBlur={() => handleFieldBlur('password')}
                          aria-invalid={!!fieldErrors.password}
                          className={`${inputClass} pr-12`}
                          placeholder="Min. 6 characters"
                          minLength="6"
                          required
                        />
                        <button
                          type="button"
                          onClick={handleTogglePasswordVisibility}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-[#FF6A00] transition-colors p-1.5 rounded-lg"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {fieldErrors.password ? (
                        <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.password}</p>
                      ) : (
                        <p className="text-xs text-slate-500">
                          Must be at least 6 characters long
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Lock className="h-3.5 w-3.5 text-[#FF6A00]" />
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          onBlur={() => handleFieldBlur('confirmPassword')}
                          aria-invalid={!!fieldErrors.confirmPassword}
                          className={`${inputClass} pr-12`}
                          placeholder="Re-enter your password"
                          required
                        />
                        <button
                          type="button"
                          onClick={handleToggleConfirmPasswordVisibility}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-[#FF6A00] transition-colors p-1.5 rounded-lg"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {fieldErrors.confirmPassword && (
                        <p className="text-xs text-red-600 font-medium" role="alert">{fieldErrors.confirmPassword}</p>
                      )}
                    </div>
                  </div>

                  {/* Benefits Section */}
                  <div className="bg-black/[0.03] border border-black/10 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-[#FF6A00]" />
                      What You'll Get
                    </h3>
                    <ul className="space-y-3">
                      {[
                        'Unified Command Center dashboard',
                        'Real-time safety compliance tracking',
                        'Team & worker management',
                        'Advanced analytics and reporting',
                      ].map((benefit) => (
                        <li
                          key={benefit}
                          className="flex items-start gap-3 text-sm text-slate-700"
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-[#FF6A00] mt-1.5 flex-shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <PlanSelector
                selectedPlan={formData.plan}
                onPlanChange={handlePlanChange}
                disabled={loading}
              />

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={loading}
                className="group relative w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 rounded-full shadow-[0_0_30px_rgba(255,106,0,0.35)] hover:shadow-[0_0_45px_rgba(255,106,0,0.55)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden mt-8"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <svg
                        className="h-5 w-5 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </span>
              </motion.button>
            </form>
            )}

            {/* Footer */}
            <div className="text-center space-y-3 pt-6 border-t border-black/10 mt-8">
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#FF6A00] hover:text-orange-300 font-semibold transition-colors"
                >
                  Sign in here
                </Link>
              </p>
              <p className="text-xs text-slate-500">
                Having trouble? Contact your site administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
